import { useState, useEffect } from 'react';
import type { StoreSearchResult } from '../../../../types/api.types';
import SettingsTab from './SettingsTab';
import SendEventTab from './SendEventTab';
import UnsyncedOrdersTab from './UnsyncedOrdersTab';
import StockOpsTab from './StockOpsTab';

interface StoreModalProps {
    isOpen: boolean;
    store: StoreSearchResult | null;
    storeBrandId: string;
    onClose: () => void;
    onOpenBrandSettings: () => void;
}

type TabId = 'settings' | 'sendEvent' | 'unsyncedOrders' | 'stock';

const TABS: { id: TabId; label: string }[] = [
    { id: 'settings', label: 'Settings' },
    { id: 'sendEvent', label: 'Quick actions' },
    { id: 'unsyncedOrders', label: 'Failed orders' },
    { id: 'stock', label: 'Stock' },
];

export default function StoreModal({
    isOpen,
    store,
    storeBrandId,
    onClose,
    onOpenBrandSettings,
}: StoreModalProps) {
    const [activeTab, setActiveTab] = useState<TabId>('settings');
    const [stockOpsRunning, setStockOpsRunning] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setActiveTab('settings');
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (stockOpsRunning) return;
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose, stockOpsRunning]);

    const handleClose = () => {
        if (stockOpsRunning) return;
        onClose();
    };

    const handleTabClick = (tabId: TabId) => {
        if (stockOpsRunning) return;
        setActiveTab(tabId);
    };

    if (!isOpen || !store) return null;

    const handleOpenBrandSettings = () => {
        onOpenBrandSettings();
    };

    const widePanel = activeTab === 'stock' || stockOpsRunning;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
                aria-hidden="true"
            />

            <div
                role="dialog"
                aria-modal="true"
                aria-label={`${store.name} settings and stock ops`}
                className={`relative bg-white rounded-2xl shadow-xl w-full ${widePanel ? 'max-w-4xl' : 'max-w-2xl'} mx-4 flex flex-col overflow-hidden max-h-[90vh]`}
            >
                <div className="flex flex-shrink-0 items-start justify-between gap-4 px-6 py-4 border-b border-[#e2e8f0]">
                    <div className="min-w-0">
                        <h2 className="text-lg font-semibold text-[#1e293b] truncate">
                            {store.name}
                        </h2>
                        <p className="text-sm text-[#64748b] mt-0.5">
                            Manage settings, send updates, fix order sync issues, and run stock ops
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={stockOpsRunning}
                        className={`w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-lg text-[#64748b] transition-colors text-xl leading-none ${
                            stockOpsRunning
                                ? 'opacity-40 cursor-not-allowed'
                                : 'hover:text-[#1e293b] hover:bg-[#f1f5f9]'
                        }`}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                <div className="flex flex-shrink-0 min-h-12 items-center border-b border-[#e2e8f0] px-6 overflow-x-auto">
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            disabled={stockOpsRunning && tab.id !== 'stock'}
                            className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
                                activeTab === tab.id
                                    ? 'border-[#6e64f0] text-[#6e64f0]'
                                    : 'border-transparent text-[#64748b] hover:text-[#1e293b]'
                            } ${stockOpsRunning && tab.id !== 'stock' ? 'opacity-40 cursor-not-allowed' : ''}`}
                            onClick={() => handleTabClick(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {activeTab === 'settings' && (
                        <SettingsTab
                            store={store}
                            storeBrandId={storeBrandId}
                            onOpenBrandSettings={handleOpenBrandSettings}
                        />
                    )}
                    {activeTab === 'sendEvent' && (
                        <SendEventTab storeId={store._id} storeBrandId={storeBrandId} />
                    )}
                    {activeTab === 'unsyncedOrders' && (
                        <UnsyncedOrdersTab storeId={store._id} storeBrandId={storeBrandId} />
                    )}
                    <div className={activeTab !== 'stock' ? 'hidden' : ''}>
                        <StockOpsTab
                            store={store}
                            storeBrandId={storeBrandId}
                            isRunning={stockOpsRunning}
                            setIsRunning={setStockOpsRunning}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
